import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

let _ = t => t,
    _t;

import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import { MONTHLY } from 'self-service-api/constants/Terms';
import styled, { css } from 'styled-components';
import Small from 'UIComponents/elements/Small';
import breakpoint from '../../../constants/Breakpoints';
import { DISCOUNTED_KEY_GETTER } from '../../../constants/pricing/DiscountedKeys';
import { useAppSelector } from '../../../reducers';
import { activeCurrencySelector } from '../../../selectors/CurrencySelectors';
import { productForSkuIdSelector } from '../../../selectors/ProductsSelectors';
import { isEligibleForLatamPromotionSelector, isEligibleForRetailPromotionSelector } from '../../../selectors/PromotionSelectors';
import { usePricingLibSelector } from '../../../selectors/SelectorUtils';
import PriceDisplay from './PriceDisplay';
const fullPriceStyles = css(["line-height:initial;padding:1px 0 2px 0;display:block;"]);
export const FullPriceContainerBlock = styled.div.withConfig({
  displayName: "HighlightedPriceDisplay__FullPriceContainerBlock",
  componentId: "zvi2o2-0"
})(["font-size:1.25rem;font-weight:500;", ""], fullPriceStyles);
export const FullPriceContainerBlockLarge = styled.div.withConfig({
  displayName: "HighlightedPriceDisplay__FullPriceContainerBlockLarge",
  componentId: "zvi2o2-1"
})(["font-size:1.375rem;font-weight:500;margin-bottom:0;", ""], fullPriceStyles);
export const PriceContainer = styled.div.withConfig({
  displayName: "HighlightedPriceDisplay__PriceContainer",
  componentId: "zvi2o2-2"
})(["line-height:initial;"]);
export const CrossedOut = styled.span.withConfig({
  displayName: "HighlightedPriceDisplay__CrossedOut",
  componentId: "zvi2o2-3"
})(["text-decoration:line-through;"]);
export const CrossedOutPrice = styled(PriceDisplay).withConfig({
  displayName: "HighlightedPriceDisplay__CrossedOutPrice",
  componentId: "zvi2o2-4"
})(["text-decoration:line-through;"]);
export const CrossedOutSubtextPrice = styled(PriceDisplay).withConfig({
  displayName: "HighlightedPriceDisplay__CrossedOutSubtextPrice",
  componentId: "zvi2o2-5"
})(["text-decoration:line-through;"]);
export const PriceSection = styled.div.withConfig({
  displayName: "HighlightedPriceDisplay__PriceSection",
  componentId: "zvi2o2-6"
})(["text-align:left;"]);
export const MicroCopy = styled(Small).withConfig({
  displayName: "HighlightedPriceDisplay__MicroCopy",
  componentId: "zvi2o2-7"
})(["display:block;", ""], breakpoint.tablet(_t || (_t = _`
    display: inline;
  `)));
export const Bold = styled.span.withConfig({
  displayName: "HighlightedPriceDisplay__Bold",
  componentId: "zvi2o2-8"
})(["font-weight:600;"]);
export const PerMonth = styled.span.withConfig({
  displayName: "HighlightedPriceDisplay__PerMonth",
  componentId: "zvi2o2-9"
})(["font-weight:500;"]);
export const HighlightedPriceDisplay = ({
  pricingInfo,
  skuId,
  nonMerchandiseIdProduct,
  wrapperStyle,
  useLargePrice,
  showingOnlyAdditionalCost = false,
  showNoAdditionalCost = false,
  showPricePerMonthPerSeat = false,
  concise = false,
  showNoSubtext = false,
  useDiscountedKey
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const showRetailStarterPrice = useAppSelector(state => skuId && isEligibleForRetailPromotionSelector(state, {
    skuId
  }));
  const currencyCode = useAppSelector(activeCurrencySelector);
  const {
    monthly: {
      fullPrice: monthlyFullPrice,
      calculatedPrice: monthlyCalculatedPrice
    },
    annually: {
      fullPrice: annualFullPrice,
      calculatedPrice: annualCalculatedPrice
    },
    offerInfo
  } = pricingInfo; // Temporary solution - only show for latam

  const showCrossedOutMonthlyFullPrice = useAppSelector(state => skuId && isEligibleForLatamPromotionSelector(state, {
    skuId
  }));
  let discountedKey = offerInfo && offerInfo.payment === MONTHLY ? showCrossedOutMonthlyFullPrice ? 'promotionPricingPerMonthBilledMonthly_jsx' : 'discountedPricingPerMonthBilledMonthly_jsx' : showCrossedOutMonthlyFullPrice ? 'promotionPricingPerYearAtCost_jsx' : 'discountedPricingPerYearAtCost_jsx';
  let normalKey = offerInfo && offerInfo.payment === MONTHLY ? showingOnlyAdditionalCost ? 'additionalCostPerMonthBilledMonthly_jsx' : 'pricingPerMonthBilledMonthly_jsx' : showingOnlyAdditionalCost ? 'additionalCostPerMonthBilledYearly_jsx' : 'pricingPerYearAtCost_jsx';

  if (isSeatsRedesignUngated) {
    discountedKey = offerInfo && showCrossedOutMonthlyFullPrice ? showPricePerMonthPerSeat ? 'promotionSeat_jsx' : 'promotion_jsx' : showPricePerMonthPerSeat ? 'discountedSeat_jsx' : 'discounted_jsx';
    normalKey = offerInfo && showingOnlyAdditionalCost ? showPricePerMonthPerSeat ? 'additionalSeat_jsx' : 'additional_jsx' : showPricePerMonthPerSeat ? 'startsAtSeat_jsx' : 'startsAt_jsx';
  } // Enable overriding discountedKey logic at component-level by setting the `useDiscountedKey` prop


  if (useDiscountedKey) {
    const getDiscountedKey = DISCOUNTED_KEY_GETTER[useDiscountedKey];
    let newKey;

    if (offerInfo) {
      newKey = getDiscountedKey(offerInfo.payment);
    } // Validate key before overriding


    if (newKey && isValidI18nKey(`merchCards.pricing.${newKey}`)) {
      discountedKey = newKey;
    }
  }

  const shouldCheckOfferInfoForDiscount = !!skuId && !showRetailStarterPrice;
  const showDiscountedCopy = !showNoAdditionalCost && !showingOnlyAdditionalCost && (shouldCheckOfferInfoForDiscount ? offerInfo && offerInfo.discount > 0 : monthlyFullPrice > monthlyCalculatedPrice); // Temporary for CRM suite where we override the price object

  const apiName = useAppSelector(state => {
    const product = skuId && productForSkuIdSelector(state, {
      skuId
    });
    return product && product.apiName;
  });
  const seleniumId = apiName || nonMerchandiseIdProduct || 'loading';

  if (concise) {
    return /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: `merchCards.pricing.concise_jsx`,
      options: {
        currencyCode,
        price: showDiscountedCopy ? monthlyCalculatedPrice : monthlyFullPrice,
        seleniumId
      },
      elements: {
        PriceDisplay
      }
    });
  }

  if (showNoAdditionalCost) {
    return /*#__PURE__*/_jsxs(PriceSection, {
      style: wrapperStyle,
      children: [/*#__PURE__*/_jsx(MicroCopy, {
        use: "help",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "merchCards.pricing.subtexts.noAdditionalCost"
        })
      }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "merchCards.pricing.pricePerMonthNoSubtext_jsx",
        options: {
          currencyCode,
          seleniumId,
          price: 0
        },
        elements: {
          PriceContainer: useLargePrice ? FullPriceContainerBlockLarge : FullPriceContainerBlock,
          PriceDisplay
        }
      })]
    });
  }

  if (showNoSubtext) {
    return /*#__PURE__*/_jsx(PriceSection, {
      style: wrapperStyle,
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `merchCards.pricing.${showPricePerMonthPerSeat ? 'pricePerMonthPerSeatNoSubtext_jsx' : 'pricePerMonthNoSubtext_jsx'}`,
        options: {
          currencyCode,
          seleniumId,
          price: monthlyCalculatedPrice
        },
        elements: {
          PriceContainer: useLargePrice ? FullPriceContainerBlockLarge : FullPriceContainerBlock,
          PriceDisplay
        }
      })
    });
  }

  return /*#__PURE__*/_jsx(PriceSection, {
    style: wrapperStyle,
    children: showDiscountedCopy ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: `merchCards.pricing.${discountedKey}`,
      options: {
        currencyCode,
        monthlyFullPrice,
        monthlyDiscountedPrice: monthlyCalculatedPrice,
        seleniumId,
        annualFullPrice,
        annualDiscountedPrice: annualCalculatedPrice
      },
      elements: {
        MicroCopy,
        FullPriceContainer: useLargePrice ? FullPriceContainerBlockLarge : FullPriceContainerBlock,
        CrossedOut,
        CrossedOutSubtextPrice,
        PriceDisplay,
        Bold,
        PriceContainer,
        PerMonth
      }
    }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: `merchCards.pricing.${normalKey}`,
      options: {
        currencyCode,
        price: monthlyCalculatedPrice,
        seleniumId,
        annualPrice: annualCalculatedPrice
      },
      elements: {
        MicroCopy,
        PriceContainer: useLargePrice ? FullPriceContainerBlockLarge : FullPriceContainerBlock,
        PriceDisplay,
        Bold,
        PerMonth
      }
    })
  });
};